import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase, TABLES, SafeIcon } from '../../lib/core';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiUser, FiTrash2, FiEdit3, FiX, FiTarget, FiAlertCircle, FiRefreshCw, FiUsers, FiCheck, FiUserPlus } = FiIcons;

const ROX_CLASSES = ['Knight', 'Crusader', 'Wizard', 'Sage', 'Hunter', 'Bard', 'Dancer', 'Assassin', 'Rogue', 'Priest', 'Monk', 'Blacksmith', 'Alchemist'];

const PilotManagement = () => {
  const { user } = useAuth();
  const [myPilots, setMyPilots] = useState([]);
  const [editingPilot, setEditingPilot] = useState(null);
  const [pilotCharacters, setPilotCharacters] = useState([]);
  const [newPilot, setNewPilot] = useState({ username: '', nickname: '' });
  const [newChar, setNewChar] = useState({ name: '', class: '', base_salary: '' });
  const [pilotMonthlyCap, setPilotMonthlyCap] = useState(0);
  const [isBasePayOnly, setIsBasePayOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);

  useEffect(() => { if (user?.id) fetchMyPilots(); }, [user?.id]);

  const notify = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const fetchMyPilots = async () => {
    setLoading(true);
    try {
      const { data: pmData, error } = await supabase
        .from(TABLES.PILOT_MASTERS)
        .select(`id, monthly_cap, is_base_pay_only, status, pilot:pilot_id (*)`)
        .eq('master_id', user.id);

      if (error) throw error;
      
      const pilots = (pmData || []).map(pm => ({
        ...pm.pilot,
        pm_id: pm.id,
        monthly_cap: pm.monthly_cap,
        is_base_pay_only: pm.is_base_pay_only,
        status: pm.status
      }));

      const activePilotIds = pilots.filter(p => p.status === 'active').map(p => p.id);
      
      if (activePilotIds.length > 0) {
        const { data: chars } = await supabase
          .from(TABLES.CHARACTERS)
          .select('*')
          .eq('master_id', user.id)
          .in('pilot_id', activePilotIds);
        
        const pilotsWithChars = pilots.map(p => ({
          ...p,
          characters: (chars || []).filter(c => c.pilot_id === p.id)
        }));
        setMyPilots(pilotsWithChars);
      } else {
        setMyPilots(pilots);
      }
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const addOrCreatePilot = async () => {
    if (!newPilot.username) return;
    setLoading(true);
    try {
      const uname = newPilot.username.trim().toLowerCase();
      // 1. Check if user exists
      let { data: existingUser } = await supabase
        .from(TABLES.USERS)
        .select('*')
        .eq('username', uname)
        .single();

      let targetUserId;

      if (!existingUser) {
        // 2. If not found, create new pilot user
        const { data: createdUser, error: createError } = await supabase
          .from(TABLES.USERS)
          .insert([{ username: uname, nickname: newPilot.nickname || uname, role: 'pilot' }])
          .select()
          .single();
        
        if (createError) throw createError;
        existingUser = createdUser;
        targetUserId = createdUser.id;
        notify(`New account created for @${uname}!`);
      } else {
        if (existingUser.role !== 'pilot') throw new Error("User exists but is not a pilot.");
        targetUserId = existingUser.id;
        notify(`Invitation sent to existing pilot @${uname}!`);
      }

      // 3. Link to master
      const { error: linkError } = await supabase.from(TABLES.PILOT_MASTERS).insert([{
        pilot_id: targetUserId,
        master_id: user.id,
        status: existingUser.nickname === uname ? 'active' : 'pending' // Auto-activate if we just created them
      }]);

      if (linkError) throw linkError;
      
      setNewPilot({ username: '', nickname: '' });
      fetchMyPilots();
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPilot = (pilot) => {
    setEditingPilot(pilot);
    setPilotMonthlyCap(pilot.monthly_cap || 0);
    setIsBasePayOnly(pilot.is_base_pay_only || false);
    fetchCharacters(pilot.id);
  };

  const fetchCharacters = async (pilotId) => {
    const { data } = await supabase.from(TABLES.CHARACTERS).select('*').eq('master_id', user.id).eq('pilot_id', pilotId);
    setPilotCharacters(data || []);
  };

  const handleUpdatePilotSettings = async () => {
    const { error: pmError } = await supabase
      .from(TABLES.PILOT_MASTERS)
      .update({ monthly_cap: parseFloat(pilotMonthlyCap), is_base_pay_only: isBasePayOnly })
      .eq('id', editingPilot.pm_id);

    if (!pmError) {
      notify("Pilot settings updated!");
      fetchMyPilots();
      setEditingPilot(null);
    }
  };

  const addCharacter = async () => {
    if (!newChar.name || !newChar.class || !newChar.base_salary) return;
    const { error } = await supabase.from(TABLES.CHARACTERS).insert([{
      pilot_id: editingPilot.id,
      master_id: user.id,
      name: newChar.name,
      class: newChar.class,
      base_salary: parseFloat(newChar.base_salary),
      salary_status: 'approved'
    }]);
    if (!error) {
      notify(`Character ${newChar.name} assigned.`);
      setNewChar({ name: '', class: '', base_salary: '' });
      fetchCharacters(editingPilot.id);
      fetchMyPilots();
    }
  };

  const removePilot = async (pilot) => {
    if (!confirm('Remove this pilot from your team? Character data will be lost.')) return;
    try {
      await supabase.from(TABLES.PILOT_MASTERS).delete().eq('id', pilot.pm_id);
      await supabase.from(TABLES.CHARACTERS).delete().eq('master_id', user.id).eq('pilot_id', pilot.id);
      notify("Pilot removed from roster.");
      fetchMyPilots();
    } catch (err) {
      notify("Failed to remove: " + err.message, 'error');
    }
  };

  const totalBaseSalary = pilotCharacters.reduce((sum, char) => sum + (Number(char.base_salary) || 0), 0);

  return (
    <div className="space-y-12 pb-20 relative">
      <AnimatePresence>
        {notification && (
          <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, scale: 0.9 }} className={`fixed top-6 right-6 z-[100] px-6 py-4 rounded-2xl shadow-2xl border flex items-center gap-3 backdrop-blur-md ${notification.type === 'success' ? 'bg-emerald-500/90 text-white border-emerald-400' : 'bg-red-500/90 text-white border-red-400'}`}>
            <SafeIcon icon={notification.type === 'success' ? FiCheck : FiAlertCircle} />
            <span className="font-black text-[10px] uppercase tracking-widest">{notification.message}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <header className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h2 className="text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
            <FiUsers className="text-indigo-600" /> Team Roster
          </h2>
          <p className="text-gray-400 font-bold mt-2 italic">Create and manage your specific pilot contracts.</p>
        </div>
        <button onClick={fetchMyPilots} className="p-4 bg-white rounded-2xl text-gray-400 hover:text-blue-600 transition-all shadow-sm border border-gray-100">
          <FiRefreshCw className={loading ? 'animate-spin' : ''} />
        </button>
      </header>

      <div className="bento-card border-blue-100/50">
        <h3 className="text-[10px] font-black text-blue-500 uppercase tracking-widest mb-6 px-2 flex items-center gap-2">
          <FiUserPlus /> Quick Create or Recruit Pilot
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <input className="apple-input bg-gray-50 shadow-sm" placeholder="Username (no spaces)" value={newPilot.username} onChange={e => setNewPilot({ ...newPilot, username: e.target.value })} />
          <input className="apple-input bg-gray-50 shadow-sm" placeholder="Display Nickname" value={newPilot.nickname} onChange={e => setNewPilot({ ...newPilot, nickname: e.target.value })} />
          <button onClick={addOrCreatePilot} disabled={loading} className="apple-button-primary !py-4">
            {loading ? 'Processing...' : 'Add Pilot to Team'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {myPilots.map(p => {
          const pilotSalary = (p.characters || []).reduce((sum, char) => sum + (Number(char.base_salary) || 0), 0);
          const isPending = p.status === 'pending';
          
          return (
            <motion.div layout key={p.id} className={`bento-card group transition-all border-2 ${isPending ? 'border-amber-100 bg-amber-50/10' : 'border-transparent hover:border-indigo-200 shadow-xl shadow-gray-200/20'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-xl ${isPending ? 'bg-amber-500' : 'bg-gray-900'}`}>
                    <SafeIcon icon={isPending ? FiTarget : FiUser} className="text-xl" />
                  </div>
                  <div>
                    <h4 className="font-black text-lg text-gray-900 truncate max-w-[120px]">{p.nickname || p.username}</h4>
                    <p className={`text-[10px] font-black uppercase tracking-widest ${isPending ? 'text-amber-500' : 'text-blue-500'}`}>
                      {isPending ? 'Invitation Sent' : (p.is_base_pay_only ? `Fixed: ₱${pilotSalary.toLocaleString()}` : `Cap: ₱${(p.monthly_cap || 0).toLocaleString()}`)}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  {!isPending && <button onClick={() => handleEditPilot(p)} className="p-2.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl shadow-sm transition-all"><FiEdit3 /></button>}
                  <button onClick={() => removePilot(p)} className="p-2.5 text-red-500 bg-red-50 hover:bg-red-100 rounded-xl shadow-sm transition-all"><FiTrash2 /></button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <AnimatePresence>
        {editingPilot && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 backdrop-blur-md bg-black/30">
            <motion.div initial={{ opacity: 0, y: 100 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 100 }} className="relative w-full max-w-4xl bg-white rounded-[3rem] p-8 md:p-12 shadow-2xl max-h-[90vh] flex flex-col overflow-hidden border border-white/50">
              <div className="flex justify-between items-center mb-10 shrink-0">
                <h3 className="text-3xl font-black truncate pr-4 text-gray-900">Config: {editingPilot.nickname || editingPilot.username}</h3>
                <button onClick={() => setEditingPilot(null)} className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-all"><FiX /></button>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 overflow-y-auto pr-4 no-scrollbar pb-10">
                <div className="space-y-8">
                  <div className="bg-gray-50 p-8 rounded-[2.5rem] space-y-6 border border-gray-100">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Earnings Model</label>
                    <div className="flex items-center justify-between p-5 bg-white rounded-2xl border border-gray-100 shadow-sm">
                      <div>
                        <p className="text-xs font-black text-gray-900">Fixed Salary Mode</p>
                        <p className="text-[9px] text-gray-400 font-bold uppercase tracking-tighter">Hide drop prices from pilot</p>
                      </div>
                      <button onClick={() => setIsBasePayOnly(!isBasePayOnly)} className={`w-14 h-7 rounded-full transition-all relative ${isBasePayOnly ? 'bg-amber-500' : 'bg-gray-200'}`} >
                        <motion.div animate={{ x: isBasePayOnly ? 28 : 2 }} className="absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow-md" />
                      </button>
                    </div>
                    <div className="relative">
                      <span className="absolute left-6 top-1/2 -translate-y-1/2 font-black text-gray-400 text-xl">₱</span>
                      <input type="number" disabled={isBasePayOnly} value={isBasePayOnly ? totalBaseSalary : pilotMonthlyCap} onChange={(e) => setPilotMonthlyCap(e.target.value)} className="apple-input !pl-12 !py-5 bg-white text-xl font-black" placeholder="Monthly Cap" />
                    </div>
                    <button onClick={handleUpdatePilotSettings} className="apple-button-primary w-full !py-5 text-xs font-black uppercase tracking-widest shadow-indigo-100">Update Contract</button>
                  </div>
                  <div className="bg-indigo-50/50 p-8 rounded-[2.5rem] border border-indigo-100/50 space-y-4">
                    <h4 className="text-[10px] font-black uppercase text-indigo-400 tracking-widest ml-2">Add Game Character</h4>
                    <input className="apple-input bg-white shadow-sm" placeholder="IGN / Name" value={newChar.name} onChange={e => setNewChar({ ...newChar, name: e.target.value })} />
                    <select className="apple-input bg-white shadow-sm" value={newChar.class} onChange={e => setNewChar({ ...newChar, class: e.target.value })}>
                      <option value="">Select Class</option>
                      {ROX_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input type="number" className="apple-input bg-white shadow-sm" placeholder="Base Salary (₱)" value={newChar.base_salary} onChange={e => setNewChar({ ...newChar, base_salary: e.target.value })} />
                    <button onClick={addCharacter} className="apple-button-primary w-full !bg-indigo-600 !py-5 text-xs font-black uppercase tracking-widest">Assign Character</button>
                  </div>
                </div>
                <div className="space-y-4">
                  <h4 className="text-[10px] font-black uppercase text-gray-400 tracking-widest ml-4">Character Roster</h4>
                  {pilotCharacters.map(char => (
                    <div key={char.id} className="p-6 bg-white border border-gray-100 rounded-3xl shadow-sm flex items-center justify-between group hover:border-indigo-200 transition-all animate-in fade-in slide-in-from-right-2">
                      <div>
                        <div className="font-black text-gray-900 text-lg">{char.name}</div>
                        <div className="text-[10px] font-black text-indigo-500 uppercase flex items-center gap-1.5">
                          {char.class} <span className="text-gray-300">•</span> ₱{char.base_salary.toLocaleString()}
                        </div>
                      </div>
                      <button onClick={() => { if (confirm('Remove character?')) supabase.from(TABLES.CHARACTERS).delete().eq('id', char.id).then(() => fetchCharacters(editingPilot.id)); }} className="p-3 text-red-500 bg-red-50 hover:bg-red-500 hover:text-white rounded-2xl transition-all shadow-sm"><FiTrash2 /></button>
                    </div>
                  ))}
                  {pilotCharacters.length === 0 && (
                    <div className="py-12 text-center border-2 border-dashed border-gray-100 rounded-[2rem] text-gray-300 font-black text-[10px] uppercase tracking-widest">No characters assigned</div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PilotManagement;